import { SEO } from "@/components/SEO";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, MapPin, Users, ArrowLeft, Gift, Check } from "lucide-react";

export default function ExperienciaDetalle() {
  const { slug } = useParams();
  const [exp, setExp] = useState(null);
  const [step, setStep] = useState("info"); // info | form | done
  const [caja, setCaja] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", consentimiento: false, es_regalo: false, regalo_para_nombre: "", regalo_para_email: "" });
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/experiencias/${slug}`).then(({ data }) => setExp(data)).catch(() => setExp("not-found"));
  }, [slug]);

  if (!exp) return <p className="max-w-3xl mx-auto py-16 md:py-24 px-6 text-center">Cargando...</p>;
  if (exp === "not-found") return (
    <div className="max-w-3xl mx-auto py-16 md:py-24 px-6 text-center">
      <p className="font-script text-4xl text-cometa-marino mb-3">No encontramos esta experiencia.</p>
      <Link to="/marca/experiencias" className="text-cometa-marino underline">Ver todas las experiencias</Link>
    </div>
  );

  const cupoLleno = exp.inscritos >= exp.cupo_maximo;

  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      const payload = { experiencia_slug: slug, caja, ...form };
      const { data } = await api.post("/inscripciones", payload);
      setResult(data); setStep("done");
      toast.success(data.lista_espera ? "Estás en lista de espera." : "Registro confirmado. Sigue el paso al pago.");
    } catch { toast.error("No pudimos completar tu inscripción."); } finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 lg:py-20">
      <SEO title={`${exp.nombre} · Utopistas`} description={(exp.descripcion || "Experiencia Utopistas para adultos mayores.").slice(0, 158)} />
      <Link to="/marca/experiencias" className="inline-flex items-center gap-2 text-cometa-marino mb-6 text-lg hover:underline" data-testid="exp-back"><ArrowLeft size={18} /> Volver al catálogo</Link>

      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="aspect-[16/9] bg-cometa-marino/10 rounded-3xl mb-8" style={exp.imagen ? { backgroundImage: `url(${exp.imagen})`, backgroundSize: "cover", backgroundPosition: "center" } : {}} />
          <div className="flex flex-wrap gap-2 mb-4"><span className="inline-block bg-cometa-dorado text-cometa-tinta text-base font-semibold px-4 py-1.5 rounded-full" data-testid="exp-detalle-area">{exp.area || exp.tipo}</span>{exp.formato && <span className="inline-block bg-cometa-marino/10 text-cometa-marino text-base font-semibold px-4 py-1.5 rounded-full" data-testid="exp-detalle-formato">{exp.formato}</span>}</div>
          <h1 className="text-4xl md:text-6xl leading-[1] mb-6">{exp.nombre}</h1>
          <div className="space-y-3 text-lg text-cometa-tinta/80 mb-8">
            {exp.fecha && <p className="flex items-center gap-3"><Calendar size={20} /> {new Date(exp.fecha).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })} {exp.hora && `· ${exp.hora}`}</p>}
            <p className="flex items-center gap-3 capitalize"><MapPin size={20} /> {exp.modalidad} {exp.modalidad === "en linea" && "· Zoom"}</p>
            <p className="flex items-center gap-3"><Users size={20} /> Cupo {exp.inscritos}/{exp.cupo_maximo}</p>
          </div>
          <p className="text-lg text-cometa-tinta/80 leading-relaxed mb-8">{exp.descripcion}</p>

          {exp.guia_nombre && (
            <div className="bg-cometa-crema border border-cometa-marino/10 rounded-2xl p-6 mb-8">
              <p className="text-sm tracking-wider text-cometa-marino/70 mb-2">NOS ACOMPAÑA</p>
              <h3 className="text-2xl mb-2">{exp.guia_nombre}</h3>
              <p className="text-cometa-tinta/80">{exp.guia_semblanza}</p>
            </div>
          )}

          {exp.libro_titulo && (
            <div className="bg-white border border-cometa-marino/10 rounded-2xl p-6">
              <p className="text-sm tracking-wider text-cometa-marino/70 mb-2">LIBRO DEL MES</p>
              <p className="text-2xl">{exp.libro_titulo}</p>
              <p className="text-cometa-tinta/60">{exp.libro_autor}</p>
            </div>
          )}
        </div>

        <aside className="lg:col-span-2">
          <div className="sticky top-24 bg-white border border-cometa-marino/15 rounded-3xl p-7 shadow-sm">
            {exp.precio ? <p className="text-4xl font-display text-cometa-marino mb-1">${exp.precio} <span className="text-base text-cometa-tinta/50">MXN</span></p> : <p className="text-xl text-cometa-tinta/60 italic mb-2">Precio próximamente</p>}
            {exp.caja_experiencia_disponible && exp.caja_experiencia_precio && (
              <p className="text-sm text-cometa-tinta/60">+ Caja Experiencia: ${exp.caja_experiencia_precio} MXN</p>
            )}

            {cupoLleno && <p className="mt-4 p-3 rounded-xl bg-cometa-carmesi/10 text-cometa-carmesi font-semibold text-center">Cupo lleno — únete a la lista de espera</p>}

            {step === "info" && (
              <button onClick={() => setStep("form")} data-testid="exp-inscribirme" className="btn mt-6 w-full bg-cometa-marino text-cometa-crema py-4 rounded-full font-semibold text-lg hover:bg-cometa-tinta transition">
                {cupoLleno ? "Unirme a la lista de espera" : "Quiero inscribirme"}
              </button>
            )}

            {step === "form" && (
              <form onSubmit={submit} className="mt-6 space-y-3">
                <input required placeholder="Tu nombre completo" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full border border-cometa-tinta/15 rounded-xl px-4 py-3 text-lg focus:outline-none focus:border-cometa-marino" data-testid="exp-form-nombre" />
                <input required type="email" placeholder="Correo" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-cometa-tinta/15 rounded-xl px-4 py-3 text-lg focus:outline-none focus:border-cometa-marino" data-testid="exp-form-email" />
                <input required placeholder="Teléfono / WhatsApp" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="w-full border border-cometa-tinta/15 rounded-xl px-4 py-3 text-lg focus:outline-none focus:border-cometa-marino" data-testid="exp-form-telefono" />
                {exp.caja_experiencia_disponible && (
                  <label className="flex items-start gap-3 p-4 bg-cometa-dorado/10 border border-cometa-dorado/30 rounded-xl cursor-pointer">
                    <input type="checkbox" checked={caja} onChange={(e) => setCaja(e.target.checked)} className="mt-1" data-testid="exp-form-caja" />
                    <div>
                      <p className="font-semibold flex items-center gap-2"><Gift size={16} /> Agregar Caja Experiencia</p>
                      <p className="text-sm text-cometa-tinta/60">El libro te lo contamos. Lo demás es sorpresa.</p>
                    </div>
                  </label>
                )}
                <label className="flex items-start gap-3 text-sm text-cometa-tinta/80 pt-2">
                  <input type="checkbox" checked={form.consentimiento} onChange={(e) => setForm({ ...form, consentimiento: e.target.checked })} className="mt-1" data-testid="exp-form-consentimiento" />
                  <span>Quiero recibir novedades y próximas experiencias.</span>
                </label>
                <button disabled={sending} data-testid="exp-form-submit" className="btn w-full bg-cometa-marino text-cometa-crema py-4 rounded-full font-semibold text-lg hover:bg-cometa-tinta transition disabled:opacity-60">
                  {sending ? "Guardando..." : "Confirmar registro"}
                </button>
                <p className="text-xs text-cometa-tinta/50 text-center">Después del registro pasamos al pago. Nada complicado.</p>
              </form>
            )}

            {step === "done" && result && (
              <div className="mt-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-cometa-marino/10 flex items-center justify-center mb-4"><Check className="text-cometa-marino" /></div>
                <p className="font-script text-3xl text-cometa-marino mb-2">¡Listo!</p>
                <p className="text-lg mb-4">{result.lista_espera ? "Estás en lista de espera. Te avisaremos si se libera un cupo." : "Tu lugar está reservado. En breve te contactaremos con los detalles de pago."}</p>
                <p className="text-xs text-cometa-tinta/50">ID: {result.inscripcion_id}</p>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
